import React, { useState, useMemo, useEffect } from "react";
import {
  Dialog, DialogContent, Typography, Button, Box,
  LinearProgress, IconButton, Stack, Paper
} from "@mui/material";
import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";
import ReplayIcon from "@mui/icons-material/Replay";
import { useAppContext } from "../context/AppContext";
import { calculateCorpusRequired } from "../utils/calculations";
import ReactECharts from "echarts-for-react";


const QUESTIONS = [
  {
    id: "objective",
    title: "1. What is your primary objective for investing?",
    options: [
      "a) Safety of capital",
      "b) Moderate growth with some risk",
      "c) Maximizing returns, even if it involves significant risk",
    ],
    riskOptions: ["Conservative", "Balanced", "Aggressive"],
  },
  {
    id: "volatility",
    title: "2. When you think about high volatility in markets, what comes to mind?",
    options: [
      "a) Better investment opportunities",
      "b) Fear of permanent loss of capital",
      "c) Volatility is part of long term investing, stay put",
    ],
    riskOptions: ["Aggressive", "Conservative", "Balanced"],
  },
  {
    id: "allocate",
    title: "3. How would you allocate your investment portfolio if given three options?",
    options: [
      "a) Mostly low-risk assets (FD, PPF, NPS etc)",
      "b) A mix of low and medium-risk assets (FD, PPF, Hybrid MF and some Equity MF)",
      "c) High-risk, high-reward assets (Mostly stocks or Equity MF).",
    ],
    riskOptions: ["Conservative", "Balanced", "Aggressive"],
  },
  {
    id: "downturn",
    title: "4. How do you feel when you hear news about a market downturn?",
    options: [
      "a) Cautious but take no immediate action",
      "b) Calm and view it as a long-term opportunity",
      "c) Anxious and consider selling my investments",
    ],
    riskOptions: ["Conservative", "Balanced", "Aggressive"],
  },
  {
    id: "rising",
    title: "5. When markets are rising rapidly, how would you feel about your investments?",
    options: [
      "a) Excited and willing to invest more to capitalise on the momentum",
      "b) Satisfied but expect consistent gains in the future.",
      "c) Relieved but I would want to book profits to lock in the gains.",
    ],
    riskOptions: ["Aggressive", "Balanced", "Conservative"],
  },
  {
    id: "fluctuates",
    title: "6. How comfortable are you with investments where the value fluctuates significantly over short periods?",
    options: [
      "a) Very uncomfortable as it makes me worry",
      "b) Doesn't affect me emotionally",
      "c) Very comfortable; I see them as opportunities.",
    ],
    riskOptions: ["Conservative", "Balanced", "Aggressive"],
  },
  {
    id: "returns",
    title: "7. Do you understand that higher returns are often associated with higher risks?",
    options: [
      "a) Yes, but I prefer to avoid risk.",
      "b) Yes, and I am willing to accept some risk for better returns.",
      "c) Yes, and I actively seek higher-risk opportunities.",
    ],
    riskOptions: ["Conservative", "Balanced", "Aggressive"],
  },
];

export default function RiskProfileForm() {
  const { state, actions } = useAppContext();
  const [open, setOpen] = useState(false);
  const [currentIdx, setCurrentIdx] = useState(0);
  const [answers, setAnswers] = useState({});

  const profileMap = {
    Conservative: { value: 20, color: "#000000", label: "Conservative" }, // light blue
    Balanced: { value: 50, color: "#000000", label: "Balanced" },        // medium blue
    Aggressive: { value: 90, color: "#000000", label: "Aggressive" },    // dark blue
  };

  const current = QUESTIONS[currentIdx];
  const total = QUESTIONS.length;

  // The corpus depends on the post-retirement return, which is driven by the risk
  // profile. ExpensesInRetirementForm (which normally owns this calculation) is
  // already unmounted by this step, so recompute here or the funding score and the
  // submit payload go out with a stale corpusRequired.
  useEffect(() => {
    const corpusRequired = calculateCorpusRequired(state);
    if (corpusRequired === null || corpusRequired === state.corpusRequired) return;
    actions.updateFormData('corpusRequired', corpusRequired);
  }, [state.riskProfile]); // eslint-disable-line react-hooks/exhaustive-deps

  const progress = useMemo(() => ((currentIdx + 1) / total) * 100, [currentIdx, total]);

  const computeProfile = (finalAnswers) => {
    let Aggressive = 0, Conservative = 0;
    QUESTIONS.forEach((q) => {
      const selected = finalAnswers[q.id];
      if (typeof selected === "number") {
        const risk = q.riskOptions[selected];
        if (risk === "Aggressive") Aggressive++;
        if (risk === "Conservative") Conservative++;
      }
    });
    if (Aggressive > 3) return "Aggressive";
    if (Conservative > 3) return "Conservative";
    return "Balanced";
  };

  const handleOptionSelect = (optionIdx) => {
    const updated = { ...answers, [current.id]: optionIdx };
    setAnswers(updated);

    if (currentIdx + 1 < total) {
      setCurrentIdx((i) => i + 1);
    } else {
      // last question → compute + save + close dialog
      const profile = computeProfile(updated);
      actions.updateFormData('riskProfile', profile); // push into context
      setOpen(false);
    }
  };

  const handleKeyDown = (e, optionIdx) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      handleOptionSelect(optionIdx);
    } else if (e.key === 'ArrowDown') {
      e.preventDefault();
      const nextIdx = optionIdx + 1;
      if (nextIdx < current.options.length) {
        const options = document.querySelectorAll('[role="radio"]');
        if (options[nextIdx]) options[nextIdx].focus();
      }
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      const prevIdx = optionIdx - 1;
      if (prevIdx >= 0) {
        const options = document.querySelectorAll('[role="radio"]');
        if (options[prevIdx]) options[prevIdx].focus();
      }
    }
  };

  const handleBack = () => {
    if (currentIdx > 0) setCurrentIdx((i) => i - 1);
  };

  const restart = () => {
    setCurrentIdx(0);
    setAnswers({});
  };

  const renderQuestion = () => (
    <>
      <Box px={3} pt={2}>
        <Stack direction="row" alignItems="center" spacing={1} mb={1}>
          {currentIdx > 0 && (
            <IconButton onClick={handleBack} size="small" aria-label="Previous question">
              <ArrowBackIosNewIcon fontSize="small" />
            </IconButton>
          )}
          <Box flexGrow={1}>
            <Typography variant="subtitle2">
              Question {currentIdx + 1} of {total}
            </Typography>
            <LinearProgress
              variant="determinate"
              value={progress}
              aria-label="Risk Profile Questionnaire"
              aria-valuetext={`Question ${currentIdx + 1} of ${total}`}
              sx={{ height: 8, borderRadius: 4, mt: 0.5 }}
            />
          </Box>
        </Stack>
      </Box>

      <DialogContent>
        <Box mb={2}>
          <Typography variant="h6" id={`question-${current.id}`}>{current.title}</Typography>
        </Box>
        <Stack spacing={1} role="group" aria-labelledby={`question-${current.id}`}>
          {current.options.map((option, idx) => (
            <Paper
              key={idx}
              role="radio"
              aria-checked={answers[current.id] === idx}
              tabIndex={answers[current.id] === idx ? 0 : -1}
              elevation={answers[current.id] === idx ? 4 : 1}
              onClick={() => handleOptionSelect(idx)}
              onKeyDown={(e) => handleKeyDown(e, idx)}
              sx={{
                p: 2,
                cursor: "pointer",
                border: answers[current.id] === idx ? "2px solid" : "1px solid rgba(0,0,0,0.1)",
                borderColor: answers[current.id] === idx ? "primary.main" : "grey.300",
                '&:focus': {
                  outline: '2px solid',
                  outlineColor: 'primary.main',
                  outlineOffset: '2px',
                }
              }}
            >
              <Typography>{option}</Typography>
            </Paper>
          ))}
        </Stack>
      </DialogContent>

      <Box px={3} pb={2}>
        <Stack direction="row" justifyContent="space-between">
          <Button onClick={() => setOpen(false)} color="inherit">Cancel</Button>
          <Button size="small" onClick={restart} startIcon={<ReplayIcon />}>Restart</Button>
        </Stack>
      </Box>
    </>
  );

  const PROFILES = ["Conservative", "Balanced", "Aggressive"];

  return (
    <>
      <Box sx={{ maxWidth: 600, mb: 2 }}>
        <Typography variant="h6" sx={{ mb: 1, color: "primary.main" }}>
          Know Your Risk Profile
        </Typography>
        <Typography variant="body1" gutterBottom>
          A 7-question assessment that takes less than a minute to personalize your investment approach.
        </Typography>
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
          <Button
            variant="contained"
            sx={{ borderRadius: 3, px: 4 }}
            onClick={() => { setOpen(true); restart(); }}
          >
            Take Assessment
          </Button>
        </Box>

        {/* OR divider */}
        <Stack direction="row" alignItems="center" spacing={2} sx={{ my: 3 }}>
          <Box sx={{ flex: 1, height: '1px', bgcolor: 'grey.300' }} />
          <Typography variant="body2" color="text.secondary">OR</Typography>
          <Box sx={{ flex: 1, height: '1px', bgcolor: 'grey.300' }} />
        </Stack>

        <Stack spacing={1}>
          {PROFILES.map((profile) => {
            const selected = state.riskProfile === profile;
            return (
              <Paper
                key={profile}
                elevation={selected ? 3 : 1}
                onClick={() => actions.updateFormData('riskProfile', profile)}
                sx={{
                  p: 2,
                  cursor: "pointer",
                  display: "flex",
                  alignItems: "center",
                  gap: 1.5,
                  border: selected ? "2px solid" : "1px solid",
                  borderColor: selected ? "primary.main" : "grey.300",
                }}
              >
                <Box sx={{
                  width: 18, height: 18, borderRadius: "50%", flexShrink: 0,
                  border: selected ? "5px solid" : "2px solid",
                  borderColor: selected ? "primary.main" : "grey.400",
                  bgcolor: selected ? "primary.main" : "transparent",
                  boxSizing: "border-box",
                }} />
                <Typography fontWeight={selected ? 600 : 400}>
                  {profile}
                </Typography>
              </Paper>
            );
          })}
        </Stack>

        {state.riskProfile && (
  <Box sx={{ mt: 2, textAlign: "center" }}>
    <Typography variant="body1" component="h2" gutterBottom>
      Your Risk Profile
    </Typography>

    <div role="img" aria-label={`Risk profile gauge showing ${state.riskProfile} risk level`}>
      <ReactECharts
        style={{ height: "300px" }}
        option={{
          series: [
            {
              type: "gauge",
              startAngle: 180,
              endAngle: 0,
              min: 0,
              max: 100,
              splitNumber: 2,
              axisLine: {
                lineStyle: {
                  width: 50, // increased arc thickness
                  color: [
                    [0.33, "#90caf9"], // light blue (Conservative)
                    [0.66, "#42a5f5"], // medium blue (Balanced)
                    [1, "#1565c0"],    // dark blue (Aggressive)
                  ],
                },
              },
              pointer: {
                icon: "path://M2 0 L-2 0 L0 -60 Z",
                length: "70%",
                width: 8,
                offsetCenter: [0, "5%"],
                itemStyle: { color: "#000000" },
              },
              axisTick: { show: false },
              splitLine: { show: false },
              axisLabel: { show: false },
              detail: {
                formatter: profileMap[state.riskProfile].label,
                fontSize: 16,
                fontWeight: 600,
                color: profileMap[state.riskProfile].color,
                offsetCenter: [0, "30%"], // position inside gauge
              },
              data: [{ value: profileMap[state.riskProfile].value }],
            },
          ],
        }}
      />
    </div>
  </Box>
)}

      </Box>

      <Dialog open={open} fullWidth maxWidth="sm" onClose={() => setOpen(false)}>
        {renderQuestion()}
      </Dialog>
    </>
  );
}


